// MCHD Room Scheduler - Block List Component
// Displays paginated list of time blocks with filtering and actions

import {
  Paper,
  Table,
  TextInput,
  Select,
  Group,
  Button,
  Text,
  Badge,
  ActionIcon,
  Menu,
  Pagination,
  Stack,
  Alert,
  Center,
  Loader,
  Grid,
  ThemeIcon,
} from '@mantine/core';
import {
  IconSearch,
  IconFilter,
  IconDots,
  IconEdit,
  IconTrash,
  IconCalendar,
  IconCheck,
  IconAlertCircle,
  IconBlockquote,
  IconRefresh,
  IconPlus,
} from '@tabler/icons-react';

// Import types
import type { 
  BlockWithStats, 
  PaginationMeta,
} from '../../api/endpoints/blocks';
import { formatBlockDuration, getBlockStatusColor, isBlockUpcoming, isBlockExpired, isBlockCurrent } from '../../api/endpoints/blocks';

// =============================================================================
// INTERFACES
// =============================================================================

interface BlockListProps {
  blocks: BlockWithStats[];
  pagination?: PaginationMeta | undefined;
  isLoading?: boolean | undefined;
  error?: any;
  filters: {
    search: string;
    isActive?: boolean | undefined;
    startDate?: string | undefined;
    endDate?: string | undefined;
    sortBy: 'name' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt' | 'isActive';
    sortOrder: 'asc' | 'desc';
  };
  onFilterChange: (filters: Partial<BlockListProps['filters']>) => void;
  onSelectBlock: (block: BlockWithStats) => void;
  onDeleteBlock: (id: string) => void;
  onUpdateBlock: (id: string, data: any) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Block List Component
 * Displays blocks with filtering, sorting, and action capabilities
 */
export function BlockList({
  blocks = [],
  pagination,
  isLoading = false,
  error,
  filters = {
    search: '',
    isActive: undefined,
    startDate: undefined,
    endDate: undefined,
    sortBy: 'createdAt' as const,
    sortOrder: 'desc' as const,
  },
  onFilterChange,
  onSelectBlock,
  onDeleteBlock,
}: BlockListProps): JSX.Element {
  // =============================================================================
  // STATE
  // =============================================================================


  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle search change
   */
  const handleSearchChange = (value: string) => {
    onFilterChange({ search: value });
  };

  /**
   * Handle status filter change
   */
  const handleStatusFilterChange = (value: string | null) => {
    const isActive = value === 'active' ? true : value === 'inactive' ? false : undefined;
    onFilterChange({ isActive });
  };

  /**
   * Handle date filter change
   */
  const handleDateFilterChange = (field: 'startDate' | 'endDate', value: string) => {
    onFilterChange({ [field]: value || undefined });
  };

  /**
   * Handle sort change
   */
  const handleSortChange = (field: string) => {
    const sortBy = field as 'name' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt';
    const sortOrder = filters.sortBy === sortBy && filters.sortOrder === 'asc' ? 'desc' : 'asc';
    onFilterChange({ sortBy, sortOrder });
  };

  /**
   * Handle page change
   */
  const handlePageChange = (page: number) => {
    // This would be handled by the parent component
    console.log('Page changed to:', page);
  };

  /**
   * Handle block selection
   */
  const handleBlockSelect = (block: BlockWithStats) => {
    onSelectBlock(block);
  };

  /**
   * Handle block edit
   */
  const handleEdit = (block: BlockWithStats) => {
    onSelectBlock(block);
  };

  /**
   * Handle block delete
   */
  const handleDelete = (id: string) => {
    onDeleteBlock(id);
  };

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' },
  ];

  // =============================================================================
  // RENDER
  // =============================================================================

  if (error) {
    return (
      <Alert
        icon={<IconAlertCircle size={16} />}
        title="Error Loading Blocks"
        color="red"
      >
        {error.message || 'An error occurred while loading blocks'}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* Filters */}
      <Paper p="md" withBorder>
        <Group mb="md">
          <ThemeIcon color="blue" variant="light">
            <IconFilter size={16} />
          </ThemeIcon>
          <Text fw={500}>Filters & Search</Text>
        </Group>
        
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <TextInput
              placeholder="Search blocks..."
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              leftSection={<IconSearch size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 2 }}>
            <Select
              placeholder="All Status"
              value={filters.isActive === true ? 'active' : filters.isActive === false ? 'inactive' : null}
              onChange={handleStatusFilterChange}
              data={statusOptions}
              clearable
              leftSection={<IconCheck size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 2 }}>
            <TextInput
              type="date"
              placeholder="Start Date"
              value={filters.startDate || ''}
              onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
              leftSection={<IconCalendar size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 2 }}>
            <TextInput
              type="date"
              placeholder="End Date"
              value={filters.endDate || ''}
              onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
              leftSection={<IconCalendar size={16} />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 2 }}>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => onFilterChange({ search: '', isActive: undefined, startDate: undefined, endDate: undefined })}
            >
              Clear
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Results Summary */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {pagination ? `Showing ${blocks.length} of ${pagination.totalItems} blocks` : `${blocks.length} blocks`}
        </Text>
        <Group gap="xs">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={() => onSelectBlock({} as BlockWithStats)}
          >
            Add Block
          </Button>
        </Group>
      </Group>

      {/* Table */}
      <Paper withBorder>
        {isLoading ? (
          <Center p="xl">
            <Stack align="center" gap="md">
              <Loader size="md" />
              <Text c="dimmed">Loading blocks...</Text>
            </Stack>
          </Center>
        ) : blocks.length === 0 ? (
          <Center p="xl">
            <Stack align="center" gap="md">
              <ThemeIcon size="xl" color="gray" variant="light">
                <IconBlockquote size={32} />
              </ThemeIcon>
              <Text c="dimmed">No blocks found</Text>
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={() => onSelectBlock({} as BlockWithStats)}
              >
                Create First Block
              </Button>
            </Stack>
          </Center>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSortChange('name')}
                >
                  <Group gap="xs">
                    Block Name
                    {filters.sortBy === 'name' && (
                      <Text size="xs" c="dimmed">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </Text>
                    )}
                  </Group>
                </Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSortChange('startDate')}
                >
                  <Group gap="xs">
                    Date Range
                    {filters.sortBy === 'startDate' && (
                      <Text size="xs" c="dimmed">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </Text>
                    )}
                  </Group>
                </Table.Th>
                <Table.Th>Duration</Table.Th>
                <Table.Th>Assignments</Table.Th>
                <Table.Th
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSortChange('isActive')}
                >
                  <Group gap="xs">
                    Status
                    {filters.sortBy === 'isActive' && (
                      <Text size="xs" c="dimmed">
                        {filters.sortOrder === 'asc' ? '↑' : '↓'}
                      </Text>
                    )}
                  </Group>
                </Table.Th>
                <Table.Th style={{ width: 80 }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {blocks.map((block) => {
                const isUpcoming = isBlockUpcoming(block.startDate);
                const isExpired = isBlockExpired(block.endDate);
                const isCurrent = isBlockCurrent(block.startDate, block.endDate, block.isActive);
                
                return (
                  <Table.Tr
                    key={block.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleBlockSelect(block)}
                  >
                    <Table.Td>
                      <Stack gap={4}>
                        <Text fw={500}>{block.name}</Text>
                        {block.description && (
                          <Text size="xs" c="dimmed" lineClamp={1}>
                            {block.description}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed">
                          ID: {block.id.slice(-8)}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        <Text size="sm">
                          {new Date(block.startDate).toLocaleDateString()} - {new Date(block.endDate).toLocaleDateString()}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatBlockDuration(block.startDate, block.endDate)}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={isCurrent ? 'blue' : isUpcoming ? 'orange' : isExpired ? 'red' : 'gray'}
                        variant="light"
                        size="sm"
                      >
                        {isCurrent ? 'Current' : isUpcoming ? 'Upcoming' : isExpired ? 'Expired' : 'Unknown'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <ThemeIcon size="sm" color="blue" variant="light">
                          <IconBlockquote size={12} />
                        </ThemeIcon>
                        <Text size="sm">{block.assignmentCount}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={getBlockStatusColor(block.isActive)}
                        variant="light"
                        size="sm"
                      >
                        {block.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" width={200}>
                        <Menu.Target>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(block);
                            }}
                          >
                            Edit Block
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(block.id);
                            }}
                          >
                            Delete Block
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination
            value={pagination.currentPage}
            onChange={handlePageChange}
            total={pagination.totalPages}
            size="sm"
          />
        </Group>
      )}
    </Stack>
  );
}

export default BlockList;
